import {useCarStockJourneyCheck} from '@hooks/useCarStockJourneyCheck';
import {useUIDuck} from '@hooks/useUIDuck';
import {useCarDetailsDuck} from '@hooks/useCarDetailsDuck';
import {useGlobalDuck} from '@hooks/useGlobalDuck';
import {useJourneyType} from '@hooks/useJourneyType';
import {useFeatureSwitch} from '@hooks/useFeatureSwitch';
import {IDealerFee} from '@components/DealerFees/DealerFeesTemplate';
import {CARTE_GRISE_FEE} from '../constants/main';
import {isBasketContinueButtonDisabled} from '@components/ContinueCTAButton/ContinueCTAButtonTemplate';
import {Pages} from '../types/pages';
import {useMemo} from 'react';
import isEmpty from 'lodash/isEmpty';

export interface UseInterestedBoxLogicProps {
    page: Pages;
}

export const useInterestedBoxLogic = ({ page }: UseInterestedBoxLogicProps) => {
    const { isStockJourney } = useCarStockJourneyCheck();
    const { isLoading } = useUIDuck();
    const { currentDeal } = useCarDetailsDuck();
    const { carteGriseError } = useGlobalDuck();
    const { isFinance } = useJourneyType();
    const {
        isBasketLegalTermsEnabled,
        isStockBasketSaveItemEnabled,
        isBrandRecommendsJourneyAllowed,
        isSaveBasketCTADisabled,
        isShareConfigurationEnabled,
        isDealerFeesEnabled,
        isCarteGriseEnabled,
    } = useFeatureSwitch();

    const carteGriseFee = currentDeal?.fullProductConfiguration?.fees?.find(
        (dealerFee: IDealerFee) => dealerFee.id === CARTE_GRISE_FEE
    );

    const hasAppliedPostalCode = carteGriseFee?.isUsed;

    const dealerFees = useMemo(
        () => (!isEmpty(currentDeal?.extraFields?.dealerFees) ? JSON.parse(currentDeal?.extraFields?.dealerFees) : []),
        [currentDeal?.extraFields?.dealerFees]
    );
    const carteGriseDealerFee = dealerFees?.find((dealerFee: IDealerFee) => dealerFee.id === CARTE_GRISE_FEE);

    const isBasketPage = page === Pages.basket;
    const isTrimPage = page === Pages.trim;
    const isDealerPage = page === Pages.dealer;

    const isGoToBasketButtonDisabled = isBasketContinueButtonDisabled(
        isLoading,
        isBasketPage,
        carteGriseFee,
        hasAppliedPostalCode,
        carteGriseError,
        isDealerFeesEnabled
    );

    const showBasketLegalTerms = isBasketLegalTermsEnabled && isFinance;

    const showSaveButton = useMemo(
        () =>
            (isSaveBasketCTADisabled && isBasketPage && !isShareConfigurationEnabled) || isTrimPage || isDealerPage
                ? false
                : isStockJourney
                ? isStockBasketSaveItemEnabled
                : !isBrandRecommendsJourneyAllowed,
        [
            isSaveBasketCTADisabled,
            isBasketPage,
            isTrimPage,
            isStockJourney,
            isShareConfigurationEnabled,
            isStockBasketSaveItemEnabled,
            isBrandRecommendsJourneyAllowed,
        ]
    );

    return {
        isStockJourney,
        isLoading,
        currentDeal,
        carteGriseError,
        isFinance,
        isBasketLegalTermsEnabled,
        isStockBasketSaveItemEnabled,
        isBrandRecommendsJourneyAllowed,
        isSaveBasketCTADisabled,
        isShareConfigurationEnabled,
        isDealerFeesEnabled,
        isCarteGriseEnabled,
        carteGriseFee,
        hasAppliedPostalCode,
        dealerFees,
        carteGriseDealerFee,
        isBasketPage,
        isGoToBasketButtonDisabled,
        showBasketLegalTerms,
        showSaveButton,
    };
};
